import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api, { BACKEND_URL } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Label } from "../components/ui/label";
import { ArrowLeft, Download, Ship, Plane, Calendar, Package, Loader2, CheckCircle2, Clock, Users, Building2, Filter, X } from "lucide-react";
import html2pdf from "html2pdf.js";

export default function InformePrevisionLlegada() {
  const navigate = useNavigate();
  const { user, hasRole } = useAuth();
  
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [destinatario, setDestinatario] = useState("admin"); // Para admin: "proveedor", "cliente" o "admin"
  
  // Estados para filtros (solo admin)
  const [filtroProveedor, setFiltroProveedor] = useState("_all");
  const [filtroCliente, setFiltroCliente] = useState("_all");
  const [filtroEmpresa, setFiltroEmpresa] = useState("_all");
  const [filtrosDisponibles, setFiltrosDisponibles] = useState({
    proveedores: [],
    clientes: [],
    empresas: []
  });

  const isAdmin = hasRole(["admin", "admin_consultor"]);

  useEffect(() => {
    fetchInforme();
  }, [destinatario, filtroProveedor, filtroCliente, filtroEmpresa]);

  const fetchInforme = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (isAdmin && destinatario && destinatario !== "admin") {
        params.append("destinatario", destinatario);
      }
      // Añadir filtros solo para admin (ignorar _all)
      if (isAdmin) {
        if (filtroProveedor && filtroProveedor !== "_all") params.append("proveedor_id", filtroProveedor);
        if (filtroCliente && filtroCliente !== "_all") params.append("cliente_id", filtroCliente);
        if (filtroEmpresa && filtroEmpresa !== "_all") params.append("empresa_id", filtroEmpresa);
      }
      const url = `/reports/prevision-llegada${params.toString() ? '?' + params.toString() : ''}`;
      const response = await api.get(url);
      setData(response.data);
      
      // Guardar filtros disponibles (solo la primera vez o cuando cambie destinatario)
      if (response.data.filtros_disponibles) {
        setFiltrosDisponibles(response.data.filtros_disponibles);
      }
      
      if (response.data.meta?.total_lotes === 0) {
        toast.info("No hay lotes pendientes de llegada");
      }
    } catch (err) {
      setError("Error al cargar el informe");
      toast.error("Error al cargar el informe");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      const [y, m, d] = dateStr.split("-");
      return `${d}/${m}/${y}`;
    } catch {
      return dateStr;
    }
  };

  const formatNumber = (value) => {
    if (!value && value !== 0) return "-";
    return new Intl.NumberFormat('es-ES', { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    }).format(value);
  };

  const getShortName = (nombre) => {
    if (!nombre || nombre === "-" || nombre === "Sin asignar") return nombre || "-";
    if (nombre.toLowerCase().includes("invermatis")) return "Invermatis";
    if (nombre.toLowerCase().includes("lg global")) return "LG Global";
    if (nombre.toLowerCase().includes("frisur")) return "Frisur";
    if (nombre.toLowerCase().includes("vexpa")) return "Vexpa";
    if (nombre.toLowerCase().includes("deepblue")) return "DeepBlue";
    if (nombre.toLowerCase().includes("japo")) return "Japo Fish";
    if (nombre.toLowerCase().includes("noriberica") || nombre.toLowerCase().includes("noribérica")) return "Noribérica";
    return nombre.split(",")[0].split(" ").slice(0, 2).join(" ");
  };

  const getFooterSummary = () => {
    if (!data?.meta) return "";
    const { rol_efectivo, proveedores, empresas, clientes, fecha_informe } = data.meta;
    
    let summary = `Previsión llegada - ${fecha_informe}`;
    
    if (rol_efectivo === "proveedor") {
      const nombresProveedores = proveedores.map(n => getShortName(n)).join(", ");
      const nombresEmpresas = empresas?.map(n => getShortName(n)).join(", ") || "-";
      summary += ` | Proveedor: ${nombresProveedores || "-"} | Cliente: ${nombresEmpresas}`;
    } else if (rol_efectivo === "cliente") {
      const nombresEmpresas = empresas?.map(n => getShortName(n)).join(", ") || "-";
      summary += ` | Proveedor: ${nombresEmpresas}`;
    } else {
      const nombresProveedores = proveedores.map(n => getShortName(n)).join(", ");
      const nombresClientes = clientes.map(n => getShortName(n)).join(", ");
      summary += ` | Proveedores: ${nombresProveedores || "-"} | Clientes: ${nombresClientes || "-"}`;
    }
    
    return summary;
  };

  const traducirMes = (mesNombre) => {
    const traducciones = {
      'January': 'Enero', 'February': 'Febrero', 'March': 'Marzo',
      'April': 'Abril', 'May': 'Mayo', 'June': 'Junio',
      'July': 'Julio', 'August': 'Agosto', 'September': 'Septiembre',
      'October': 'Octubre', 'November': 'Noviembre', 'December': 'Diciembre'
    };
    let resultado = mesNombre;
    for (const [en, es] of Object.entries(traducciones)) {
      resultado = resultado.replace(en, es);
    }
    return resultado;
  };

  const limpiarFiltros = () => {
    setFiltroProveedor("_all");
    setFiltroCliente("_all");
    setFiltroEmpresa("_all");
  };

  const hayFiltrosActivos = (filtroProveedor && filtroProveedor !== "_all") || 
                            (filtroCliente && filtroCliente !== "_all") || 
                            (filtroEmpresa && filtroEmpresa !== "_all");

  const handleDownloadPdf = async () => {
    const element = document.getElementById("informe-prevision-container");
    if (!element) return;

    const fechaHoy = new Date().toISOString().split("T")[0].replace(/-/g, "");
    let nombreArchivo = `Prevision_Llegada_${fechaHoy}`;
    if (destinatario) {
      nombreArchivo += `_${destinatario}`;
    }
    nombreArchivo += ".pdf";

    const opt = {
      margin: [15, 10, 20, 10],
      filename: nombreArchivo,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
      pagebreak: { mode: ["avoid-all", "css", "legacy"] },
    };

    toast.info("Generando PDF...");
    try {
      await html2pdf().set(opt).from(element).save();
      toast.success("PDF descargado correctamente");
    } catch (error) {
      toast.error("Error al generar el PDF");
    }
  };

  const rol_efectivo = data?.meta?.rol_efectivo;

  const getTableHeaders = () => {
    const baseHeaders = ["Nº Lote", "Producto", "Kg Netos", "Contenedor", "Llegada"];
    
    if (rol_efectivo === "proveedor") {
      return [...baseHeaders, "Cliente"];
    } else if (rol_efectivo === "cliente") {
      return [...baseHeaders, "Proveedor"];
    } else {
      return [...baseHeaders, "Proveedor", "Cliente"];
    }
  };

  // Renderizar una fila de lote
  const renderLoteRow = (lote, idx) => (
    <TableRow key={idx} className="hover:bg-slate-50">
      <TableCell className="font-mono font-medium text-xs">
        <div className="flex items-center gap-1">
          {lote.documentacion_completa && (
            <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0" title="Documentación completa" />
          )}
          <span>{lote.numero_lote}</span>
        </div>
      </TableCell>
      <TableCell className="text-xs">{lote.producto_nombre || "-"}</TableCell>
      <TableCell className="font-mono text-xs text-right">{formatNumber(lote.peso_neto)}</TableCell>
      <TableCell className="font-mono text-xs">
        <div className="flex items-center gap-1">
          {lote.tipo_envio === "maritimo" ? (
            <Ship className="w-3 h-3 text-blue-500 shrink-0" />
          ) : (
            <Plane className="w-3 h-3 text-amber-500 shrink-0" />
          )}
          <span>{lote.contenedor}</span>
        </div>
      </TableCell>
      <TableCell className="text-slate-600 text-xs">{formatDate(lote.llegada_estimada)}</TableCell>
      
      {rol_efectivo === "proveedor" && (
        <TableCell className="text-xs">{getShortName(lote.cliente_nombre)}</TableCell>
      )}
      {rol_efectivo === "cliente" && (
        <TableCell className="text-xs">{getShortName(lote.proveedor_nombre)}</TableCell>
      )}
      {(rol_efectivo === "admin" || rol_efectivo === "admin_consultor") && (
        <>
          <TableCell className="text-xs">{getShortName(lote.proveedor_nombre)}</TableCell>
          <TableCell className="text-xs">{getShortName(lote.cliente_nombre)}</TableCell>
        </>
      )}
    </TableRow>
  );

  return (
    <div className="space-y-6" data-testid="informe-prevision-page">
      {/* Cabecera */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate("/reports")}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Previsión de Llegada</h1>
            <p className="text-slate-500 mt-1">Lotes pendientes ordenados por fecha de llegada estimada</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchInforme} disabled={loading}>
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Calendar className="w-4 h-4 mr-2" />}
            Actualizar
          </Button>
          {data?.meta?.total_lotes > 0 && (
            <Button onClick={handleDownloadPdf} className="bg-slate-900 hover:bg-slate-800">
              <Download className="w-4 h-4 mr-2" />
              Descargar PDF
            </Button>
          )}
        </div>
      </div>

      {/* Selector de destinatario para Admin */}
      {isAdmin && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center gap-4">
            <Label className="text-blue-800 font-medium">¿Para quién es este informe?</Label>
            <Select value={destinatario} onValueChange={setDestinatario}>
              <SelectTrigger className="w-[220px] bg-white">
                <SelectValue placeholder="Vista completa (Admin)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="admin">
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    Vista completa (Admin)
                  </div>
                </SelectItem>
                <SelectItem value="proveedor">
                  <div className="flex items-center gap-2">
                    <Building2 className="w-4 h-4" />
                    Para el Proveedor
                  </div>
                </SelectItem>
                <SelectItem value="cliente">
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    Para el Cliente
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
            {destinatario && destinatario !== "admin" && (
              <span className="text-sm text-blue-600">
                Mostrando datos como los vería un {destinatario}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Filtros avanzados para Admin */}
      {isAdmin && (
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <Filter className="w-4 h-4 text-slate-600" />
            <Label className="text-slate-700 font-medium">Filtrar resultados</Label>
            {hayFiltrosActivos && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={limpiarFiltros}
                className="ml-auto text-slate-500 hover:text-slate-700"
              >
                <X className="w-4 h-4 mr-1" />
                Limpiar filtros
              </Button>
            )}
          </div>
          <div className="flex flex-wrap gap-4">
            {/* Filtro por Proveedor */}
            <div className="flex flex-col gap-1.5">
              <Label className="text-xs text-slate-500">Proveedor</Label>
              <Select value={filtroProveedor} onValueChange={setFiltroProveedor}>
                <SelectTrigger className="w-[200px] bg-white" data-testid="filtro-proveedor">
                  <SelectValue placeholder="Todos los proveedores" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_all">Todos los proveedores</SelectItem>
                  {filtrosDisponibles.proveedores.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.nombre}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Filtro por Cliente */}
            <div className="flex flex-col gap-1.5">
              <Label className="text-xs text-slate-500">Cliente</Label>
              <Select value={filtroCliente} onValueChange={setFiltroCliente}>
                <SelectTrigger className="w-[200px] bg-white" data-testid="filtro-cliente">
                  <SelectValue placeholder="Todos los clientes" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_all">Todos los clientes</SelectItem>
                  {filtrosDisponibles.clientes.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.nombre}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Filtro por Empresa */}
            <div className="flex flex-col gap-1.5">
              <Label className="text-xs text-slate-500">Empresa</Label>
              <Select value={filtroEmpresa} onValueChange={setFiltroEmpresa}>
                <SelectTrigger className="w-[200px] bg-white" data-testid="filtro-empresa">
                  <SelectValue placeholder="Todas las empresas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_all">Todas las empresas</SelectItem>
                  {filtrosDisponibles.empresas.map((e) => (
                    <SelectItem key={e.id} value={e.id}>
                      {e.nombre}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          {/* Indicador de filtros activos */}
          {hayFiltrosActivos && (
            <div className="mt-3 pt-3 border-t border-slate-200 text-xs text-slate-600">
              <span className="font-medium">Filtros activos: </span>
              {filtroProveedor && filtroProveedor !== "_all" && (
                <span className="inline-flex items-center bg-slate-200 px-2 py-0.5 rounded mr-2">
                  Proveedor: {filtrosDisponibles.proveedores.find(p => p.id === filtroProveedor)?.nombre}
                </span>
              )}
              {filtroCliente && filtroCliente !== "_all" && (
                <span className="inline-flex items-center bg-slate-200 px-2 py-0.5 rounded mr-2">
                  Cliente: {filtrosDisponibles.clientes.find(c => c.id === filtroCliente)?.nombre}
                </span>
              )}
              {filtroEmpresa && filtroEmpresa !== "_all" && (
                <span className="inline-flex items-center bg-slate-200 px-2 py-0.5 rounded mr-2">
                  Empresa: {filtrosDisponibles.empresas.find(e => e.id === filtroEmpresa)?.nombre}
                </span>
              )}
            </div>
          )}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center h-64">
          <div className="spinner" />
        </div>
      )}

      {/* Error */}
      {error && !loading && (
        <div className="text-center py-12 bg-white rounded-sm border border-red-200">
          <p className="text-red-600">{error}</p>
        </div>
      )}

      {/* Sin datos */}
      {!loading && !error && data?.meta?.total_lotes === 0 && (
        <div className="text-center py-12 bg-white rounded-sm border border-slate-200">
          <Package className="w-12 h-12 mx-auto mb-4 text-slate-300" />
          <h3 className="text-lg font-medium text-slate-900 mb-2">No hay lotes pendientes</h3>
          <p className="text-slate-500">No se encontraron lotes pendientes</p>
        </div>
      )}

      {/* Contenido del informe */}
      {!loading && data?.meta?.total_lotes > 0 && (
        <div id="informe-prevision-container" className="bg-white p-6 rounded-lg border border-slate-200">
          {/* Cabecera con logos */}
          <div className="mb-6 pb-4 border-b border-slate-200">
            <div className="flex items-center justify-between">
              {/* Logos */}
              <div className="flex items-center gap-4 flex-wrap">
                {data.meta.logos?.filter(l => l.url).length > 0 ? (
                  data.meta.logos.filter(l => l.url).map((logo, idx) => {
                    const baseUrl = BACKEND_URL;
                    // Asegurarse de que la URL del logo sea correcta
                    let logoUrl = logo.url;
                    if (!logoUrl.startsWith('http')) {
                      // Si es una ruta relativa, añadir /api al path para acceder al backend
                      if (logoUrl.startsWith('/uploads')) {
                        logoUrl = `${baseUrl}/api${logoUrl}`;
                      } else {
                        logoUrl = `${baseUrl}${logoUrl}`;
                      }
                    }
                    return (
                      <div key={idx} className="flex flex-col items-center">
                        <img 
                          src={logoUrl} 
                          alt={logo.nombre}
                          className="h-14 object-contain"
                          onError={(e) => { 
                            // Si falla la carga, mostrar el nombre en su lugar
                            e.target.style.display = 'none';
                            e.target.nextSibling && (e.target.nextSibling.className = 'text-sm font-medium text-slate-700 bg-slate-100 px-3 py-2 rounded');
                          }}
                        />
                        <span className="text-xs text-slate-500 mt-1">{getShortName(logo.nombre)}</span>
                      </div>
                    );
                  })
                ) : data.meta.logos?.length > 0 ? (
                  data.meta.logos.map((logo, idx) => (
                    <div key={idx} className="flex flex-col items-center bg-slate-100 px-4 py-2 rounded">
                      <span className="text-sm font-medium text-slate-700">{getShortName(logo.nombre)}</span>
                    </div>
                  ))
                ) : (
                  <div className="text-slate-400 text-sm italic">Sin logos configurados</div>
                )}
              </div>
              
              {/* Título */}
              <div className="text-right">
                <h2 className="text-lg font-bold text-slate-900">PREVISIÓN DE LLEGADA</h2>
                <p className="text-xs text-slate-500">Consulta realizada el día: {data.meta.fecha_informe}</p>
              </div>
            </div>
          </div>

          {/* Resumen general */}
          <div className="mb-4 flex items-center gap-6 text-xs text-slate-600">
            <div className="flex items-center gap-2">
              <Package className="w-4 h-4" />
              <span><strong>{data.meta.total_lotes}</strong> lotes pendientes</span>
            </div>
            <div className="flex items-center gap-2">
              <span><strong>{formatNumber(data.meta.total_peso_neto)}</strong> Kg totales</span>
            </div>
            {data.meta.total_sin_fecha > 0 && (
              <div className="flex items-center gap-2 text-amber-600">
                <Clock className="w-4 h-4" />
                <span><strong>{data.meta.total_sin_fecha}</strong> sin fecha estimada</span>
              </div>
            )}
          </div>

          {/* Tablas agrupadas por mes */}
          {Object.entries(data.lotes_por_mes).map(([mesKey, mesData], mesIdx) => (
            <div key={mesKey} className={mesIdx > 0 ? "mt-6 pt-4 border-t border-slate-100" : ""}>
              {/* Separador de mes con resumen */}
              {Object.keys(data.lotes_por_mes).length > 1 && (
                <div className="mb-3 flex items-center justify-between">
                  <h3 className="text-xs font-semibold text-slate-700 bg-slate-50 px-3 py-2 rounded inline-flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    {traducirMes(mesData.mes_nombre)}
                  </h3>
                  <div className="text-xs text-slate-500 bg-slate-50 px-3 py-2 rounded">
                    <span className="font-medium">{mesData.total_lotes}</span> lotes · <span className="font-medium">{formatNumber(mesData.total_peso_neto)}</span> Kg
                  </div>
                </div>
              )}
              
              <Table>
                <TableHeader>
                  <TableRow>
                    {getTableHeaders().map((header, idx) => (
                      <TableHead 
                        key={idx}
                        className="bg-slate-100 text-slate-600 font-semibold uppercase text-xs tracking-wider"
                      >
                        {header}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {mesData.lotes.map((lote, idx) => renderLoteRow(lote, idx))}
                </TableBody>
              </Table>
            </div>
          ))}

          {/* Sección de lotes SIN FECHA */}
          {data.lotes_sin_fecha?.length > 0 && (
            <div className="mt-8 pt-6 border-t-2 border-amber-200">
              <div className="mb-3 flex items-center justify-between">
                <h3 className="text-xs font-semibold text-amber-700 bg-amber-50 px-3 py-2 rounded inline-flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Pendiente fecha estimada de llegada
                </h3>
                <div className="text-xs text-amber-600 bg-amber-50 px-3 py-2 rounded">
                  <span className="font-medium">{data.lotes_sin_fecha.length}</span> lotes · <span className="font-medium">{formatNumber(data.meta.total_peso_sin_fecha)}</span> Kg
                </div>
              </div>
              
              <Table>
                <TableHeader>
                  <TableRow>
                    {getTableHeaders().map((header, idx) => (
                      <TableHead 
                        key={idx}
                        className="bg-amber-50 text-amber-700 font-semibold uppercase text-xs tracking-wider"
                      >
                        {header}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.lotes_sin_fecha.map((lote, idx) => renderLoteRow(lote, idx))}
                </TableBody>
              </Table>
            </div>
          )}

          {/* Pie de página */}
          <div className="mt-8 pt-4 border-t border-slate-200 text-xs text-slate-500">
            <p>{getFooterSummary()}</p>
          </div>
        </div>
      )}
    </div>
  );
}
